import React from 'react';

type FormatMessageTextProps = {
  text: string;
};

/**
 * Format message text with basic styling (italic, bold)
 */
export function formatMessageText({ text }: FormatMessageTextProps) {
  if (!text) return null;
  
  try {
    // Find italic text: *text*
    // Find bold text: **text**
    // Convert line breaks to <br> tags
    
    const parts = [];
    let lastIndex = 0;
    let index = 0;
    
    // Find bold text: **text**
    const boldRegex = /\*\*(.*?)\*\*/g;
    let boldMatch;
    
    // Process the text for bold formatting
    while ((boldMatch = boldRegex.exec(text)) !== null) {
      // Add regular text before the bold section
      if (boldMatch.index > lastIndex) {
        parts.push(processItalic(text.substring(lastIndex, boldMatch.index)));
      }
      
      // Add <br /> before the bold text if not the first part
      // if (lastIndex > 0) {
      //   parts.push(<br key={`bold-br-before-${index}`} />);
      // }
      // Add the bold text
      parts.push(<strong key={`bold-${index}`} className="opacity-40">**{boldMatch[1]}**</strong>);
      lastIndex = boldMatch.index + boldMatch[0].length;
      // Add <br /> if not the last part
      // if (lastIndex < text.length) {
      //   parts.push(<br key={`bold-br-${index}`} />);
      // }
      index++;
    }
    
    // Add the remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(processItalic(text.substring(lastIndex)));
    }
    
    return (
      <div className="whitespace-pre-wrap break-words">
        {parts.length > 0 ? parts : text}
      </div>
    );
  } catch (error) {
    // Fallback for older browsers that don't support certain regex features
    if (import.meta.env.DEV) console.warn('Text formatting failed, falling back to plain text:', error);
    return (
      <div className="whitespace-pre-wrap break-words">
        <span>{processNewLines(text)}</span>
      </div>
    );
  }
}

/**
 * Process italic formatting
 */
function processItalic(text: string) {
  if (!text) return null;
  
  try {
    const parts = [];
    let index = 0;
    
    // Find italic text: *text* 
    // Since bold (**text**) is processed first, we only need to handle single asterisks
    // Split by asterisks and process alternating segments as italic
    const segments = text.split('*');
    
    for (let i = 0; i < segments.length; i++) {
      if (i % 2 === 0) {
        // Even indices are regular text
        if (segments[i]) {
          parts.push(<span key={`text-${index}`}>{processNewLines(segments[i])}</span>);
        }
      } else {
        // Odd indices are italic text (between asterisks)
        if (segments[i]) {
          parts.push(<em key={`italic-${index}`} className="opacity-40">*{segments[i]}*</em>);
        }
      }
      index++;
    }
    
    return parts.length > 0 ? parts : text;
  } catch (error) {
    if (import.meta.env.DEV) console.warn('Text formatting failed, falling back to plain text:', error);
    // Fallback to unprocessed text
    return <span>{processNewLines(text)}</span>;
  }
}

/**
 * Process new lines in text
 */
function processNewLines(text: string) {
  if (!text) return null;
  
  const parts = text.split('\n');
  return parts.map((part, i) => (
    <React.Fragment key={`line-${i}`}>
      <span>{part}</span>
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
}
